import useVisibleElement from '@/scenes/DynamicHome/hooks/useVisibleElement'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

interface VisibleElementProps {
  offset?: string
  children: React.ReactNode
  defaultDisplay?: boolean
  height?: number
  fallback?: React.ReactNode
  dataName?: string
  timer?: number
}

const VisibleElement = memo(
  ({
    offset,
    children,
    defaultDisplay,
    height = 150,
    fallback = null,
    dataName,
    timer,
  }: VisibleElementProps) => {
    const [displayContent, setDisplayContent] = useState(
      defaultDisplay || false
    )
    const [minHeight, setMinHeight] = useState(height)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const setVisible = useCallback(() => {
      setMinHeight(0)
      setDisplayContent(true)
    }, [])

    useEffect(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      if (timer && timer > 0 && !displayContent) {
        timerRef.current = setTimeout(() => {
          setVisible()
          timerRef.current = null
        }, timer)
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
      }
    }, [timer, displayContent, setVisible])

    const { elementRef } = useVisibleElement({
      rootMargin: offset || '300px',
      onVisible: setVisible,
    })

    return (
      <div
        ref={elementRef}
        style={{ minHeight: minHeight, width: '100%' }}
        data-name={dataName}
      >
        {displayContent ? children : fallback}
      </div>
    )
  }
)

VisibleElement.displayName = 'VisibleElement'

export default VisibleElement
